import { ApplicationInsights, Exception, type ITelemetryItem } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { buildErrorFingerprint } from '@/services/errorFingerprint';

/**
 * React plugin for Application Insights
 * This enables automatic React component tracking and routing
 */
export const reactPlugin = new ReactPlugin();

/**
 * Telemetry initializer that adds a stable `fingerprint` property to exceptions collected
 * automatically (uncaught errors and unhandled promise rejections). Exceptions logged through the
 * logger already carry one.
 */
function addExceptionFingerprint(item: ITelemetryItem): void {
  if (item.baseType !== Exception.dataType || item.data?.fingerprint || item.baseData?.properties?.fingerprint) {
    return;
  }

  const details = item.baseData?.exceptions?.[0];
  item.data = {
    ...item.data,
    fingerprint: buildErrorFingerprint({ name: details?.typeName ?? '', message: details?.message ?? '' }),
  };
}

/**
 * Initializes Application Insights for browser-based telemetry
 *
 * @param connectionString - Application Insights connection string (from environment)
 * @returns Initialized ApplicationInsights instance or null if no connection string provided
 */
export function initializeAppInsights(connectionString?: string): ApplicationInsights | null {
  if (!connectionString) {
    console.warn('[AppInsights] No connection string provided. Telemetry will not be sent.');
    return null;
  }

  const appInsights = new ApplicationInsights({
    config: {
      connectionString,
      extensions: [reactPlugin],
      extensionConfig: {
        [reactPlugin.identifier]: {},
      },

      // Automatic tracking
      enableAutoRouteTracking: true, // Track route changes automatically
      enableRequestHeaderTracking: true, // Track request headers
      enableResponseHeaderTracking: true, // Track response headers
      disableFetchTracking: false, // Track fetch() API calls
      disableAjaxTracking: false, // Track XMLHttpRequest calls

      // CORS settings - disable correlation to avoid CORS issues with local dev
      // Correlation headers can cause CORS preflight failures
      enableCorsCorrelation: false,
      correlationHeaderExcludedDomains: ['localhost:7072', 'localhost:7071', '127.0.0.1'],

      // Performance settings
      maxBatchInterval: 5000, // Send telemetry every 5 seconds
      maxBatchSizeInBytes: 102400, // Max batch size: 100KB
      samplingPercentage: 100, // Sample 100% of telemetry (adjust for high-traffic apps)

      // Error tracking
      disableExceptionTracking: false, // Track JavaScript exceptions
      autoTrackPageVisitTime: true, // Track time spent on each page

      // Console logging (disable in production for cleaner output)
      loggingLevelConsole: import.meta.env.DEV ? 1 : 0, // 1 = WARN, 0 = OFF
      loggingLevelTelemetry: 1, // Log warnings to telemetry
    },
  });

  appInsights.loadAppInsights();
  appInsights.addTelemetryInitializer(addExceptionFingerprint);
  console.log('[AppInsights] Application Insights initialized successfully');

  return appInsights;
}
